import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { flowRight } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import moment from 'moment'
import {
    getVehicleVertical,
    getVerticalName,
    getVehiclePriceEstimateModalUrl,
    getVehicleCreateAdPath,
    isVehicleOld,
    vehicleHasAd,
    VEHICLE_STATUS
} from 'lib/utils/store/vehicle'
import { inflect } from 'lib/translate'
import { AD_STATUS } from 'lib/ad'
import { urlWithLangParam } from 'lib/helpers'
import { getLocale } from 'lib/utils/getLocale'

import Link from 'components/Link'
import Button from 'components/UI/Button'

import './price-cloud.pcss'

const PriceCloud = ({ vehicle, notResponsive, location, withUpdateLink, onClose }) => {
    const { t, i18n } = useTranslation(['vehicleProfile', 'inflection', 'vehicles'])
    const verticalPronoun = inflect(t, getVerticalName(t, getVehicleVertical(vehicle)), 'pronoun')
    const vertical = getVehicleVertical(vehicle)
    const estimatedAt = moment(vehicle.estimatedAt).format('l')

    const sellAction = (vertical) => {
        if (isVehicleOld(vehicle) && !vehicleHasAd(vehicle)) return null

        return (
            <div className="action-wrapper">
                {!vehicle.adUrl ? (
                    <>
                        <Link
                            className="button button--centered"
                            to={`${getLocale(i18n.language)}${getVehicleCreateAdPath(vehicle.id)}`}
                            trackName="createAd"
                        >
                            {t(`vehicles:actions.sellIn.${vertical}.linkText`)}
                        </Link>
                        <Button
                            className="button button--secondary button--centered"
                            trackName="stillConsidering"
                            onClick={onClose}
                        >
                            {t('vehicles:actions.sellIn.stillConsidering')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Link
                            className="button button--centered"
                            to={urlWithLangParam(vehicle.adUrl, i18n.language)}
                            trackName="viewAd"
                        >
                            {t('vehicles:actions.viewAd')}
                        </Link>
                        <Link
                            className="button button--secondary button--centered"
                            to={urlWithLangParam(vehicle.adEditUrl, i18n.language)}
                            disabled={vehicle.adStatus !== AD_STATUS.FORSALE}
                            trackName="editAd"
                        >
                            {t('vehicles:actions.editAd')}
                        </Link>
                    </>
                )}
            </div>
        )
    }

    return (
        <div
            className={classNames({
                'price-cloud--not-responsive': notResponsive,
            })}
        >
            <div
                className={classNames('price-cloud__open-link', {
                    'price-cloud__open-link-modal': !withUpdateLink,
                })}
            >
                {!withUpdateLink && <span>{t('vehicles:pricePrediction.priceCloudOpenLink.description')}</span>}
            </div>
            <span>
                {withUpdateLink ? (
                    <Trans i18nKey="vehicles:pricePrediction.infoText.priceCloudWithUpdate" vertical={{ vertical: verticalPronoun }} date={{ date: estimatedAt }}>
                        {vehicle.status !== VEHICLE_STATUS.SOLD ? (
                            <Link
                                to={getVehiclePriceEstimateModalUrl(location, vehicle.id)}
                                trackName="editPricePrediction"
                            >
                                {t('vehicles:pricePrediction.cta.updatePriceEstimate')}
                            </Link>
                        ) : (
                            'Update estimate'
                        )}
                        - price estimate done at {{ date: estimatedAt }}. Estimate based on vehicles sold at {{ vertical: verticalPronoun }}.
                    </Trans>
                ) : (
                    <>
                        {t('vehicles:pricePrediction.infoText.priceCloud', { vertical: verticalPronoun })}
                        {sellAction(vertical)}
                    </>
                )}
            </span>
        </div>
    )
}

PriceCloud.propTypes = {
    vehicle: PropTypes.object.isRequired,
    notResponsive: PropTypes.bool,
}

export default flowRight(
    withRouter,
)(PriceCloud)
